import { useState, useCallback, useMemo } from "react";

export function useBulkSelection<T extends { id: string }>(items: T[] | undefined) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (!items) return;
    setSelectedIds((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((i) => i.id));
    });
  }, [items]);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const allSelected = useMemo(
    () => (items?.length ?? 0) > 0 && selectedIds.size === (items?.length ?? 0),
    [items, selectedIds],
  );

  const someSelected = useMemo(
    () => selectedIds.size > 0 && selectedIds.size < (items?.length ?? 0),
    [items, selectedIds],
  );

  return {
    selectedIds,
    toggle,
    toggleAll,
    clear,
    allSelected,
    someSelected,
    count: selectedIds.size,
  };
}
